import React, { useEffect, useId, useState } from 'react';
import { Box, Button, Collapse, Icon, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import Papa from 'papaparse';

function CsvPreview({ source, name }) {
    const [isOpen, setIsOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const panelId = useId();

    useEffect(() => {
        if (!source || !isOpen) return;

        let active = true;
        setPreview(null);
        Papa.parse(source, {
            download: typeof source === 'string',
            chunkSize: typeof source === 'string' ? 64 * 1024 : undefined,
            preview: 6,
            skipEmptyLines: true,
            complete: ({ data }) => {
                if (active) setPreview({ headers: data[0] || [], rows: data.slice(1, 6) });
            },
            error: () => {
                if (active) setPreview({ error: 'Could not load a preview for this CSV.' });
            },
        });
        return () => { active = false; };
    }, [source, isOpen]);

    if (!source) return null;

    return (
        <Box mt={4} borderWidth="1px" borderRadius="md" overflow="hidden" w="100%">
            <Button
                type="button"
                variant="ghost"
                borderRadius="none"
                w="100%"
                h="auto"
                px={3}
                py={2}
                bg="gray.50"
                justifyContent="space-between"
                onClick={() => setIsOpen((open) => !open)}
                aria-expanded={isOpen}
                aria-controls={panelId}
            >
                <Text fontSize="sm" fontWeight="semibold" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={name}>
                    Preview CSV{name ? `: ${name}` : ''}
                </Text>
                <Icon as={FiChevronDown} ml={2} flexShrink={0} transform={isOpen ? 'rotate(180deg)' : undefined} />
            </Button>
            <Collapse in={isOpen} animateOpacity>
                <Box id={panelId}>
                    <Text px={3} py={2} fontSize="xs" color="gray.600">First 5 rows</Text>
                    {preview?.error ? (
                        <Text px={3} py={3} fontSize="sm" color="red.600">{preview.error}</Text>
                    ) : preview?.headers?.length ? (
                        <Box overflowX="auto" maxH="220px" overflowY="auto">
                            <Table size="sm" variant="simple" whiteSpace="nowrap">
                                <Thead>
                                    <Tr>{preview.headers.map((header, index) => <Th key={index}>{header || `Column ${index + 1}`}</Th>)}</Tr>
                                </Thead>
                                <Tbody>
                                    {preview.rows.map((row, rowIndex) => (
                                        <Tr key={rowIndex}>
                                            {preview.headers.map((_, columnIndex) => (
                                                <Td key={columnIndex} maxW="220px" overflow="hidden" textOverflow="ellipsis" title={row[columnIndex] || ''}>
                                                    {row[columnIndex] || '-'}
                                                </Td>
                                            ))}
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                            {!preview.rows.length && <Text px={3} py={3} fontSize="sm">No data rows to preview.</Text>}
                        </Box>
                    ) : (
                        <Text px={3} py={3} fontSize="sm">{preview ? 'This CSV is empty.' : 'Loading preview...'}</Text>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

export default CsvPreview;
